import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { GenericErrorModal } from '../../../../components/errors/GenericErrorModal';
import { ACTION_MODAL_TYPES, ActionModal } from '../../../../DesignSystem/ActionModal';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { useStore } from '../../../../store';
import { CreateNewProductForm } from '../../InvoiceCreation/components/SectionServiceItems/CreateNewProductForm';
import { useCreateNewServiceItem } from '../../InvoiceCreation/hooks/useCreateNewServiceItem';
import { useFetchServiceItems } from '../../InvoiceCreation/hooks/useFetchServiceItems';
import { useUpdateServiceItem } from '../../InvoiceCreation/hooks/useUpdateServiceItem';
import { ItemsTableHeader } from '../shared/ItemsTableHeader';
import { ProductsTable } from './components/ProductsTable';
import { TEXTS } from './constants';
import { useDeleteProduct } from './hooks/useDeleteProduct';
import { Layout } from '../../../../components/Layout/Layout';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

enum ProductsActivePopup {
  DeleteItem,
  EditItem,
  CreateItem,
  MenuItem,
  None,
}

export const ServicesAndProductsHub = () => {
  const { DELETE_POPUP, MAIN_TITLE, SEARCH_BAR_PLACEHOLDER, NEW_ITEM_BUTTON } = TEXTS.SERVICES_AND_PRODUCTS_HUB;
  const { t } = useTranslation();
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const { getPersonalAccount } = useStore((state) => state);
  const { accountExternalId } = getPersonalAccount();
  const [activePopup, setActivePopup] = useState<ProductsActivePopup | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t: errorsEN } = useTranslation('errorsEN');

  const {
    data: allProducts,
    refetch: refetchProducts,
    isLoading: isLoadingProducts,
    isRefetching: isRefetchingProducts,
  } = useFetchServiceItems({
    customerId: externalId,
    accountExternalId,
    onSuccess: () => {},
    onError: () => {
      setErrorMessage('Something went wrong');
    },
  });
  const { mutateAsync: mutateCreateItem, isLoading: isCreatingItem } = useCreateNewServiceItem();
  const { mutateAsync: mutateAsyncUpdateItem, isLoading: isSavingItem } = useUpdateServiceItem();
  const { mutateAsync: mutateDeleteProduct, isLoading: isDeletingProduct } = useDeleteProduct();
  const filteredProducts = () =>
    allProducts?.filter((product) => product.name.toLowerCase().includes(searchText.toLowerCase()));
  const invoicesPermission = featureAccess(null, LimitedFeatureType.Invoices);

  const reloadProducts = () => {
    refetchProducts().then((r) => {
      setSearchText('');
      setSelectedProductId(null);
      setActivePopup(ProductsActivePopup.None);
      setShowEmptyState(r.data?.length === 0);
    });
  };
  useEffect(() => {
    reloadProducts();
  }, []);
  const prepareData = (data: any) => {
    let amount: number = 0;
    if (typeof data?.amount?.value === 'number') {
      amount = data?.amount?.value;
    } else {
      amount = Number(data?.amount?.value?.replace(/[^0-9.]/g, ''));
    }
    return {
      customerId: externalId,
      accountExternalId,
      serviceItemId: selectedProductId || '',
      payload: {
        name: data.name.value,
        amount,
        description: data.itemDescription.value,
      },
    };
  };
  const handleSaveClick = (data: any) => {
    mutateAsyncUpdateItem(prepareData(data)).then(() => {
      reloadProducts();
    });
  };
  const handleCreateClick = (data: any) => {
    if (isCreatingItem) {
      return;
    }
    mutateCreateItem(prepareData(data)).then(() => {
      reloadProducts();
    });
  };

  const handleDeleteClick = () => {
    const variables = {
      customerId: externalId,
      accountExternalId,
      serviceItemId: selectedProductId || '',
    };
    mutateDeleteProduct(variables).then(() => {
      reloadProducts();
    });
  };
  const createNewForm = (
    <CreateNewProductForm
      initialName=''
      isLoading={isCreatingItem || isRefetchingProducts}
      onSubmit={handleCreateClick}
      onClose={() => setActivePopup(ProductsActivePopup.None)}
    />
  );

  const editForm = (
    <CreateNewProductForm
      initialName=''
      initialItemData={allProducts?.find((product) => product.serviceItemId === selectedProductId)}
      isLoading={isSavingItem || isRefetchingProducts}
      onSubmit={handleSaveClick}
      onClose={() => setActivePopup(ProductsActivePopup.None)}
    />
  );
  const deletePopup = (
    <ActionModal
      title={t(DELETE_POPUP.TITLE)}
      description={t(DELETE_POPUP.DESCRIPTION)}
      mainButtonTitle={t(DELETE_POPUP.DELETE_BUTTON)}
      cancelButtonTitle={t(DELETE_POPUP.CANCEL_BUTTON)}
      onSubmit={handleDeleteClick}
      onCancel={() => setActivePopup(ProductsActivePopup.None)}
      isLoading={isDeletingProduct || isRefetchingProducts}
      modalType={ACTION_MODAL_TYPES.DESTRUCTIVE}
    />
  );
  const errorModal = errorMessage && (
    <UserMessage onClick={() => setErrorMessage(null)} id=''>
      <GenericErrorModal
        title={errorsEN('GENERIC_MODAL.TITLE')}
        description={errorsEN('GENERIC_MODAL.SUBTITLE')}
        buttonTitle={errorsEN('GENERIC_MODAL.CTA')}
        onActionClick={() => setErrorMessage(null)}
      />
    </UserMessage>
  );
  return (
    <Layout wrapperClassName='pt-[55px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <div>
        {activePopup === ProductsActivePopup.CreateItem && createNewForm}
        {activePopup === ProductsActivePopup.EditItem && selectedProductId != null && editForm}
        {activePopup === ProductsActivePopup.DeleteItem && deletePopup}
        {errorModal}
        <ItemsTableHeader
          title={t(MAIN_TITLE)}
          searchPlaceholder={t(SEARCH_BAR_PLACEHOLDER)}
          addButtonTitle={t(NEW_ITEM_BUTTON)}
          addButtonDisabled={invoicesPermission !== FeatureAccess.READ_WRITE}
          onSearch={(text) => setSearchText(text)}
          onAddNewClick={() => setActivePopup(ProductsActivePopup.CreateItem)}
          totalProductsCount={allProducts?.length || 0}
        />
        {isRefetchingProducts || isLoadingProducts ? (
          <Skeleton count={10} height={60} />
        ) : (
          <ProductsTable
            products={filteredProducts() || []}
            onDeleteClick={(product) => {
              setSelectedProductId(product.serviceItemId || null);
              setActivePopup(ProductsActivePopup.DeleteItem);
            }}
            onEditClick={(product) => {
              setSelectedProductId(product.serviceItemId || null);
              setActivePopup(ProductsActivePopup.EditItem);
            }}
            onAddNewClick={() => setActivePopup(ProductsActivePopup.CreateItem)}
            showEmptyState={showEmptyState}
          />
        )}
      </div>
    </Layout>
  );
};
